/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { CompanionCard } from "components/Common/index";
import { Button, Heading, Stack, Text } from "components/index";
import useRouting from "hooks/useRouting";
import { useEffect } from "react";
import toast from "react-hot-toast";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useInvitation } from "state/InvitationContext";
import { useUI } from "state/UIContext";
import PageLayout from "templates/PageLayout";
import applySuffix from "utils/applySuffix";
import classNames from "classnames";
import { useEnvs } from "state/EnvironmentsContext/EnvContext";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

const Registration = () => {
  const { t } = useTranslation();
  const { event: EVENT } = useEnvs();
  const { navigateWithEvent } = useEventRouting();
  const { openModal, closeModal } = useUI();
  const { attendees, isLoadingAttendees } = useInvitation({
    fetchAttendees: true,
    hardFetch: true,
  });

  const user = attendees?.[0];
  const { shouldEnterCompanionsPage, resolveGuestSelectionRoute } =
    useRouting();

  // BG-22958: when the user has no companions there is no guest to select,
  // so skip this screen and continue to wherever it would have sent them.
  // Attendees are [mainUser, ...companions, ...emptySlots], so a single entry
  // means there is only the user.
  useEffect(() => {
    if (isLoadingAttendees || attendees?.length !== 1) return;
    const nextRoute = resolveGuestSelectionRoute(attendees);
    if (nextRoute !== ROUTES.onboard.registration) {
      navigateWithEvent(nextRoute, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingAttendees, attendees]);

  const SkipButton = ({ className }) => {
    const { t } = useTranslation();
    if (!user?.is_personal_form_completed) return null;
    return (
      <div className={className}>
        <Button
          variant="tertiary"
          size="lg"
          className="md:!min-w-[140px]"
          onClick={() => {
            pushToDataLayer({
              event: "guest_selection_skip",
              screen_name: "guest selection",
              journey_step: "guest selection",
            });
            const isAllCompleted = attendees.every(
              (companion) =>
                companion.hasOwnProperty("is_personal_form_completed") &&
                companion.is_personal_form_completed === true,
            );
            if (isAllCompleted) {
              closeModal();
              return navigateWithEvent(ROUTES.onboard.done);
            }
            openModal({
              content: (
                <div className="max-w-[430px] p-10 -bg--theme-colors-surface-neutral-level04  border -border--theme-colors-border-neutral-07 -rounded-inner-container">
                  <Stack className="gap-y-4">
                    <Heading
                      as="h3"
                      className="text-xl -text--theme-colors-text-title"
                    >
                      {t("registration.skipMSG")}
                    </Heading>
                    <Text className="-text--theme-colors-text-body ">
                      {t("registration.completeMSG")}
                    </Text>
                    <div className="flex w-full gap-x-4 mt-5">
                      <Button
                        fullWidth
                        variant="secondary"
                        size="md"
                        onClick={() => {
                          pushToDataLayer({
                            event: "guest_selection_cancel",
                            screen_name: "guest selection",
                            journey_step: "guest selection",
                          });
                          closeModal();
                        }}
                      >
                        {t("registration.cancelLabel")}
                      </Button>

                      <Button
                        fullWidth
                        variant="primary"
                        size="md"
                        onClick={() => {
                          pushToDataLayer({
                            event: "guest_selection_continue",
                            screen_name: "guest selection",
                            journey_step: "guest selection",
                          });
                          closeModal();
                          navigateWithEvent(ROUTES.onboard.done);
                        }}
                      >
                        {t("registration.continueLabel")}
                      </Button>
                    </div>
                  </Stack>
                </div>
              ),
            });
          }}
        >
          {t("registration.skipLabel")}
        </Button>
      </div>
    );
  };
  const handleSelectCompanion = (companion) => {
    let id = companion.hash_id || companion.id;
    if (!id) {
      id = "new";
    }
    navigateWithEvent(ROUTES.onboard.personalInfo(id));
  };

  useEffect(() => {
    shouldEnterCompanionsPage();
  }, []);
  useEffect(() => {
    if (isLoadingAttendees) {
      toast.loading("Loading...", {
        duration: 1000,
      });
    }
  }, [isLoadingAttendees]);

  return (
    <PageLayout
      title={t("registration.title")}
      // we need it for production issue
      // note={(isSportico || isBlink) ? "" : t('registration.description')}
      note={true ? "" : t("registration.description")}
      hasPolicy
    >
      <Stack className="gap-y-10 mt-10">
        <div className="flex justify-between">
          <Heading
            as="h3"
            className="-text--theme-colors-text-title-brand self-center"
          >
            {t("registration.guestInstructions")}
          </Heading>
          <SkipButton />
        </div>
        <div
          className={classNames(
            "flex flex-wrap justify-center gap-x-8 lg:gap-x-16 lg:justify-start gap-y-16",
          )}
        >
          {!isLoadingAttendees &&
            attendees?.map((companion, i) => {
              const companionDetails = { ...companion };
              if (!companionDetails.hasOwnProperty("id")) {
                // i because user is at index 0
                const companionNumber = i;
                companionDetails.first_name = applySuffix(companionNumber);
                companionDetails.last_name = "Companion";
              }
              return (
                <CompanionCard
                  key={i}
                  variant="onboard"
                  companion={companionDetails}
                  handleSelectCompanion={() => handleSelectCompanion(companion)}
                  state={
                    companion.hasOwnProperty("is_personal_form_completed") &&
                    companion.is_personal_form_completed === true
                      ? "completed"
                      : "pending"
                  }
                />
              );
            })}
        </div>
      </Stack>
    </PageLayout>
  );
};
export default Registration;
